import { useSimpleCampaigns } from "@/api/campaign.api";
import { useTrialPeriod } from "@/hooks/use-trial-period";
import { Route } from "@/routes/__root";
import { Route as BuyCreditsRoute } from "@/routes/_dashboard/buy-credits";
import { type FileRoutesByTo } from "@/routeTree.gen";
import { useAuth } from "@/services/auth.service";
import { getCreditStats } from "@/services/credits.service";
import { dateToRelative, diffInSeconds } from "@/utils/dates";
import { Link, useLocation } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { UserRoundCog, Zap } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  Button,
  DialogTrigger,
  Button as RACButton,
} from "react-aria-components";
import { useProductFruitsApi } from "react-product-fruits";
import { Avatar } from "../generic/Avatar";
import { Focusable, Tooltip } from "../generic/Tooltip";
import { Logo } from "../icons/Logo";
import { TrialCTAModal } from "../trial/TrialCTAModal";

type TRoutes = keyof FileRoutesByTo;

const formatTrialPeriodDate = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "long",
}).format;

export function TopBar() {
  const { user, claims } = useAuth();
  const trialPeriod = useTrialPeriod();
  const { appConfiguration } = Route.useRouteContext();
  const simpleCampaigns = useSimpleCampaigns();
  const location = useLocation();
  const helpCenterRef = useRef<HTMLButtonElement>(null);
  const [hasSetLifeRing, setHasSetLifeRing] = useState(false);

  const creditStats = useMemo(
    () => getCreditStats(appConfiguration),
    [appConfiguration],
  );

  const navItems = [
    ...(!simpleCampaigns || simpleCampaigns.length
      ? ([
          { label: "Kampagner", to: "/campaigns" },
          { label: "Leads", to: "/leads" },
          { label: "Performance", to: "/performance" },
        ] satisfies { label: string; to: TRoutes }[])
      : [{ label: "Kampagner", to: "/start" }]),
  ];

  const remainingCredits = () => {
    let text = "";
    let reason: "notEnoughCredits" | "riskOfRunningOut" = "notEnoughCredits";
    let neededCredits: number | undefined;

    if (appConfiguration.creditsRemaining <= 10) {
      text =
        appConfiguration.creditsRemaining === 0
          ? "Du er løbet tør for credits"
          : "Du er ved at løbe tør for credits";
    } else if (creditStats.creditsNeededRestOfPeriod) {
      text = "Undgå at løbe tør for credits";
      reason = "riskOfRunningOut";
      neededCredits = creditStats.creditsNeededRestOfPeriod;
    }

    if (!text) return null;

    const navigateOptions = {
      to: location.pathname,
      search: location.search,
      state: location.state,
    };

    return (
      <Link
        className="cursor-pointer"
        to="/buy-credits"
        state={{
          routeStates: {
            [BuyCreditsRoute.id]: {
              reason,
              neededCredits,
              allowPrepaidCredits: true,
              closeNavigateOptions: navigateOptions,
              successNavigateOptions: navigateOptions,
            },
          },
        }}
      >
        <motion.div
          whileHover={{
            scale: 1.03,
            transition: { duration: 0.2, ease: "easeInOut" },
          }}
          className="from-theme-gradient-from to-theme-gradient-to flex items-center gap-1 rounded-full bg-gradient-to-r from-70% p-1 text-xs shadow-md"
        >
          <div className="rounded-full bg-white p-1">
            <Zap size={14} className="text-theme-gold" />
          </div>
          <span className="pr-2 font-semibold text-white uppercase">
            {text}
          </span>
        </motion.div>
      </Link>
    );
  };

  useProductFruitsApi(
    (api) => {
      if (hasSetLifeRing || !helpCenterRef.current) return;

      api.inAppCenter.attachToCustomElement(helpCenterRef.current);
      setHasSetLifeRing(true);
    },
    [helpCenterRef],
  );

  return (
    <div className="flex h-9 shrink-0 items-center px-4 text-white">
      <div className="flex h-full items-center gap-x-11">
        <Logo className="w-16 leading-none" />

        <ul className="flex h-full items-center gap-x-10 text-xs leading-none font-medium uppercase">
          {navItems.map((link) => (
            <li
              key={link.label}
              className="flex items-center text-sm leading-none"
            >
              <Link to={link.to}>{link.label}</Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="ml-auto flex items-center gap-x-6">
        {!!claims.impersonating && (
          <Tooltip tooltipText="Du kontrollerer i øjeblikket denne bruger. Luk fanen for at afgive kontrol.">
            <Focusable className="flex h-8 items-center gap-x-2 rounded bg-amber-500 px-4 text-xs font-semibold text-white uppercase">
              <UserRoundCog size={14} />
              Du kontrollerer {user.name}
            </Focusable>
          </Tooltip>
        )}

        {trialPeriod && (
          <DialogTrigger>
            <Tooltip
              tooltipTriggerProps={{
                delay: 0,
                closeDelay: 0,
              }}
              tooltipText={`Din prøveperiode startede d. ${formatTrialPeriodDate(
                trialPeriod.fromDate,
              )} og ${
                trialPeriod.isExpired ? "udløb" : "udløber"
              } d. ${formatTrialPeriodDate(trialPeriod.toDate)}`}
            >
              <RACButton className="colors-glass backdrop-blur-glass hover:text-theme-active flex h-8 cursor-pointer items-center rounded px-4 text-xs font-medium uppercase">
                {trialPeriod.isExpired
                  ? "Prøveperiode udløbet"
                  : trialPeriod.daysRemaining === 1
                    ? "Din prøveperiode udløber i dag"
                    : `Din prøveperiode ${diffInSeconds(trialPeriod.toDate, new Date()) <= 0 ? "udløb" : "udløber"} ${dateToRelative(trialPeriod.toDate)}`}
              </RACButton>
            </Tooltip>

            <TrialCTAModal
              title={[
                trialPeriod.isExpired
                  ? "Din prøveperiode er udløbet."
                  : "Din prøveperiode udløber snart.",
                "Kontakt os for at få adgang til Lasso Sales uden begrænsninger.",
              ].join(" ")}
            />
          </DialogTrigger>
        )}

        <ul className="flex items-center gap-x-4">
          {remainingCredits()}
          {user && (
            <li className="flex gap-x-2">
              <Button
                ref={helpCenterRef}
                id="customLifeRingLauncher"
                className="colors-active flex size-8 cursor-pointer items-center justify-center rounded-full font-[sans-serif] text-lg font-medium"
              >
                ?
              </Button>
              <Link to="/settings/profile">
                <Avatar user={user} />
              </Link>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
